/* np-cart-size.js — C428 (Justin 2026-09-28): "in staging id like to add an edit size feature in cart
     1. this has to keep sizes proportional
     2. it needs to update all line item properties and perks sheet
     3. it needs to add line item property note from reorder of.... to say something like similar reorder new size requested"
   + "this has to incorporate the same size guidelines we use when processing size on an order where we say too much detail etc. We will
     just block them and tell them to pick a bigger size (warning should show what min width has to be for the proportional height to work)"

   A patch line's Size row gets "Edit size". Width and height stay locked to the line's own ratio. Every entry is judged the way the
   product page's Size step judges it (sizeFirstJudge in np-emb2.js, pxSizeJudge in patch-express.js):
     - the product's size window (custom.min_width / custom.max_width_height, the builder's SIZE_MIN / SIZE_MAX), per side; out of range =
       blocked, with the nearest in-range size for this design's proportions (never a silently clamped side — C229m)
     - fine detail at the new width: the Patch Gate service with judgeIn (the PX path — the cart has no classifier of its own), the same
       monotonic bisection on a 0.5" grid, so a block names the smallest width that stitches and its proportional height
     - no width verdict from the service (error / timeout) = pass, fail-open, like PX
   Saving (only from an ok verdict):
     - picks the size tier the builder would (the smallest numeric variant at least as big as the mean of W and H)
     - copies every line property, rewrites _Width (in) / _Height (in)
     - rebuilds the build sheets (_AI Proof = AI render + stats, _Upload = digital preview + stats) from the line's own base images with
       the builder's composer (np-spec-sheet.js), so the printed stats match the new size
     - notes the change on a REORDER only: _reorder becomes "Similar reorder of order #… — new size requested …" (a purchased
       design's "Design previously used on #PATCH-…" becomes "Design previously used on #PATCH-… — similar reorder, new size
       requested …", C456); a normal order gets no
       note (Justin 9-28: "If a customer edits size on a normal order nothing changes")
     - writes the line (same tier: /cart/change; new tier: /cart/add then remove the old line) and reloads the cart */
(function () {
  'use strict';
  if (window.__npCartSize) return;
  window.__npCartSize = true;

  /* the Patch Gate service — the same endpoint np-emb2.js (NP_GATE_THEME_MAP) and Patch Express (data-px-gate) post to */
  var GATE_URL = 'https://6rkg1olal7.execute-api.us-east-1.amazonaws.com/';
  /* legacy template suffix → the 2-0 type key the gate rules are keyed on: a copy of SUFFIX_ALIAS (+ PTYPE.family) in np-emb2.js. Only
     used when the line carries no build sheet — a sheet's filename already names the builder's own TYPE_KEY. */
  var SUFFIX_ALIAS = {
    'embroidered-patches': 'embroidery-2-0', 'chenile-patches': 'chenille-2-0', 'woven-patches': 'woven-2-0', 'rubber-patches': 'rubber-pvc-2-0',
    'leather-patches': 'leather-2-0', 'sublimated-patches': 'fullcolor-2-0', '3d-embroidered-patches': '3demb-2-0', 'silicone-patches': 'silicone-2-0',
    'faux-leather-2': 'fauxleather-2-0', 'print-and-stitch-patches': 'print-and-stitch-2-0', 'ninja-flex': 'metallic-flex-2-0',
    'full-color-flex': 'fullcolor-flex-2-0', 'matte-black-flex': 'matte-black-flex-2-0', 'matte-white-flex': 'matte-white-flex-2-0',
    'chrome-flex': 'chrome-flex-2-0', 'tackle-twill': 'tackle-twill-2-0', 'keychains': 'keychains-2-0'
  };
  var FAMILY = { 'woven-labels': 'woven-2-0' };
  /* PTYPE.name per type (PATCH_TYPES in np-emb2.js): the sheet's Patch Type row and the block message read exactly as the builder's */
  var NAMES = {
    'embroidery-2-0': 'Embroidered', 'print-and-stitch-2-0': 'Print & Stitch', 'fullcolor-2-0': 'Printed', 'rubber-pvc-2-0': 'PVC / Rubber',
    'woven-2-0': 'Woven', 'woven-labels': 'Woven Label', 'keychains-2-0': 'Keychain', 'leather-2-0': 'Leather', 'fauxleather-2-0': 'Faux Leather',
    'silicone-2-0': 'Silicone', '3demb-2-0': '3D Embroidered', 'chenille-2-0': 'Chenille', 'metallic-flex-2-0': 'Metallic Flex',
    'fullcolor-flex-2-0': 'Full Color Flex', 'matte-black-flex-2-0': 'Matte Black Flex', 'matte-white-flex-2-0': 'Matte White Flex', 'chrome-flex-2-0': 'Chrome Flex'
  };

  var SS = function () { return window.npSpecSheet || null; };
  var toHost = function (u) { return (u || '').replace(/https:\/\/[^/]*ninjauploads[^/]*\.s3\.amazonaws\.com/i, 'https://ninjauploads-production.imgix.net'); };
  var r2 = function (x) { return Math.round(x * 100) / 100; };
  var fmt = function (n) { return (Math.round(n * 100) / 100).toString(); };
  var q4 = function (x, up) { return (up ? Math.ceil(x * 4 - 1e-9) : Math.floor(x * 4 + 1e-9)) / 4; };
  var inch = function (w, h) { return '<b>' + fmt(w) + '″ × ' + fmt(h) + '″</b>'; };
  function j(u, o) { return fetch(u, o).then(function (r) { if (!r.ok) throw new Error(u + ' ' + r.status); return r.json(); }); }
  function post(u, body) { return j(u, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'same-origin', body: JSON.stringify(body) }); }
  function isSheet(v) { return /--spec-dk[a-z0-9]+-/.test(String(v || '')); }

  /* ---- what the gate judges: the line's artwork with the toggles the builder applied to it (applyToggleParams) ---- */
  function lineTypeKey(props, suffix) {
    /* the builder's own TYPE_KEY: from the sheet's filename when the line has one, else the product's template suffix */
    var m = /ai-proof-(.+?)--spec-dk/.exec(String(props['_AI Proof'] || '')) || /ai-proof-(.+?)--spec-dk/.exec(String(props['_Upload'] || ''));
    return (m && m[1] !== 'patch') ? m[1] : (SUFFIX_ALIAS[suffix] || suffix || '');
  }
  function nounFor(name) {
    /* "an embroidered patch", "a PVC / Rubber patch" — acronyms keep their capitals */
    var n = (/patch|label|keychain/i.test(name) ? name : name + ' patch').split(' ').map(function (wd) { return /^[A-Z0-9]{2,}$/.test(wd) ? wd : wd.toLowerCase(); }).join(' ');
    return (/^[aeiou]/i.test(n) ? 'an ' : 'a ') + n;
  }
  function judgeUrl(props) {
    var raw = String(props['_Original Image'] || '');
    if (!raw && !isSheet(props['_Upload'])) raw = String(props['_Upload'] || '');
    var u = toHost(raw.split('?')[0]);
    if (u.indexOf('imgix') === -1) return ''; /* only our upload pipeline's files — anything else goes unjudged (fail-open) */
    var q = [];
    if (/\.(pdf|ai|eps|svg)$/i.test(u)) q.push('fm=png', 'w=1200');
    if (/^yes$/i.test(String(props['_Crop & Trim'] || ''))) q.push('trim=colorUnlessAlpha');
    if (/^yes$/i.test(String(props['_Remove Background'] || ''))) q.push('bg-remove=true');
    if (/gram-edit/i.test(u)) q.push('cs=strip');
    return u + (q.length ? '?' + q.join('&') : '');
  }
  function gateAt(ctx, W) {
    /* one verdict per width, memoised; null = the service gave no width verdict (fail-open) */
    var k = W.toFixed(2);
    if (ctx.mem[k]) return ctx.mem[k];
    if (!ctx.judgeUrl || !ctx.typeKey) return Promise.resolve(null);
    var ctl = (typeof AbortController === 'function') ? new AbortController() : null, timer = ctl ? setTimeout(function () { try { ctl.abort(); } catch (e) {} }, 12000) : null;
    ctx.mem[k] = fetch(GATE_URL, { method: 'POST', mode: 'cors', credentials: 'omit', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ imageUrl: ctx.judgeUrl, typeKey: ctx.pvc ? 'embroidery-2-0' : ctx.typeKey, /* C441: PVC detail judged with the detail rules (the gate treats PVC as photo-only) */ env: 'live', surface: 'cart-size', judgeIn: W, minIn: ctx.min > 0.5 ? ctx.min : 0 }), signal: ctl ? ctl.signal : undefined })
      .then(function (r) { return r.json(); })
      .then(function (res) {
        if (timer) clearTimeout(timer);
        if (!res || !res.ok || typeof res.judgedIn === 'undefined') return null;
        var out = { fine: res.verdict === 'fineDetail', dense: res.verdict === 'dense' };
        if (out.dense) ctx.dense = true; /* C435: dense is a property of the design, not of one width */
        ctx.at[k] = out;
        return out;
      })
      .catch(function () { if (timer) clearTimeout(timer); return null; });
    return ctx.mem[k];
  }
  function judge(ctx, W, H) {
    /* sizeFirstJudge / pxSizeJudge, with the fix sizes carried as width AND the proportional height */
    var my = ++ctx.seq, mn = ctx.min, mx = ctx.max, r = ctx.ratio, name = ctx.noun;
    if (W < mn - 1e-9 || H < mn - 1e-9) {
      var wMin = q4(Math.max(mn, mn / r), true), hMin = Math.max(mn, r2(wMin * r));
      if (wMin > mx + 1e-9 || hMin > mx + 1e-9) return Promise.resolve({ state: 'block', text: 'This design’s proportions don’t fit our ' + fmt(mn) + '″–' + fmt(mx) + '″ size range.' });
      var minRes = { state: 'block', fixW: wMin, fixH: hMin, text: inch(W, H) + ' is under our <b>' + fmt(mn) + '″</b> minimum per side. Keeping your design’s proportions, the smallest we can make is ' + inch(wMin, hMin) + '.' };
      /* the smallest in-range size may itself be too small for the detail — then name the size that actually works, in one step */
      return judge(ctx, wMin, hMin).then(function (res2) {
        if (!res2) return null;
        if (res2.state === 'warn' && res2.fixW) minRes.text += ' For this design\u2019s fine detail we recommend at least <b>' + fmt(res2.fixW) + '″ wide</b> (' + inch(res2.fixW, res2.fixH) + ').'; /* C435 */
        return minRes;
      });
    }
    if (W > mx + 1e-9 || H > mx + 1e-9) {
      var wMax = q4(Math.min(mx, mx / r), false), hMax = Math.min(mx, r2(wMax * r));
      return Promise.resolve({ state: 'block', fixW: wMax, fixH: hMax, text: inch(W, H) + ' is over our <b>' + fmt(mx) + '″</b> maximum per side. Keeping your design’s proportions, the largest we can make is ' + inch(wMax, hMax) + '.' });
    }
    /* C435 (Justin 2026-09-28): "edit cart should warn them but still let them continue" — fine detail and dense detail are an amber heads-up
       with the recommended size; Update size stays available. The product's min/max per side stay hard limits. C433 wording: only
       embroidered / 3D / woven / chenille say "stitch". */
    var stitched = /^(embroidery-2-0|3demb-2-0|woven-2-0|chenille-2-0)$/.test(String(ctx.typeKey || '')), clean = stitched ? 'stitch cleanly' : 'come through cleanly';
    var okRes = { state: 'ok', text: 'Looks good at ' + inch(W, H) + (stitched ? ' — stitches cleanly.' : '.') };
    if (!ctx.judgeUrl || !ctx.typeKey) return Promise.resolve(okRes);
    /* MONOTONIC PHYSICS (C229i / C230c): a design that stitches at the calibrated 3" frame stitches at every larger width, one that fails
       at W fails at every smaller width, and any known pass answers every width above it — the answer is the smallest passing width on
       the 0.5" grid, found by bisection */
    var BASE = 3, top = Math.min(mx, 12, q4(mx / r, false));
    var knownPassBelow = function (x) { var kp = Infinity; Object.keys(ctx.at).forEach(function (k) { if (!ctx.at[k].fine && parseFloat(k) < x) kp = Math.min(kp, parseFloat(k)); }); return kp; };
    var fineAt = function (x) { return gateAt(ctx, x).then(function (g) { return !!(g && g.fine) && !(knownPassBelow(x) < x); }); };
    var noneRes = { state: 'warn', text: 'Heads up: this design has fine detail that may not ' + clean + ' at any size. You can still update the size. We will contact you if necessary.' }; /* C439 */
    var blockAt = function (minW) {
      var hB = r2(minW * r);
      if (hB > mx + 1e-9) return noneRes;
      return { state: 'warn', fixW: minW, fixH: hB, text: 'Heads up: this design has fine detail that may not ' + clean + ' at ' + inch(W, H) + '. We recommend at least <b>' + fmt(minW) + '″ wide</b> (' + inch(minW, hB) + ') — or you can keep this size. We will contact you if necessary.' }; /* C439 */
    };
    /* C441 (Justin 2026-09-28, "I want the same printed pvc logic everywhere"): PVC patches / keychains with fine detail are made as printed
       rubber PVC — no warning, an info line, and the save sets _Printed PVC: Yes (cleared when the new size is clean) */
    var pvcRes = { state: 'ok', printedPvc: true, text: 'Your design has a lot of fine detail, so we\u2019ll create it as a <b>printed rubber PVC ' + (/keychains/.test(String(ctx.typeKey || '')) ? 'keychain' : 'patch') + '</b> at ' + inch(W, H) + ' \u2014 this will allow you to keep all your details!' };
    if (ctx.pvc) { noneRes = pvcRes; blockAt = function () { return pvcRes; }; }
    var denseRes = ctx.pvc ? pvcRes : { state: 'warn', text: 'Heads up: this design has a lot of detail and shading that may not ' + clean + '. You can still update the size. We will contact you if necessary.' }; /* C439 */
    var bisect = function (lo, hi) { /* lo fails, hi passes */
      if (hi - lo <= 0.5 + 1e-9) return Promise.resolve(blockAt(hi));
      var mid = Math.round(((lo + hi) / 2) * 2) / 2; if (mid <= lo) mid = lo + 0.5; if (mid >= hi) mid = hi - 0.5;
      return fineAt(mid).then(function (fm) { if (my !== ctx.seq) return null; return fm ? bisect(mid, hi) : bisect(lo, mid); });
    };
    if (ctx.dense) return Promise.resolve(denseRes);
    if (W >= knownPassBelow(Infinity)) return Promise.resolve(okRes);
    return gateAt(ctx, BASE).then(function (g3) {
      if (my !== ctx.seq) return null;
      if (g3 && g3.dense) return denseRes; /* C435: dense detail — the same deferred verdict the product page raises at size (C432) */
      var fine3 = !!(g3 && g3.fine) && !(knownPassBelow(BASE) < BASE);
      if (W >= BASE && !fine3) return okRes;
      return (Math.abs(W - BASE) < 1e-9 ? Promise.resolve(fine3) : fineAt(W)).then(function (fineW) {
        if (my !== ctx.seq) return null;
        if (!fineW) return okRes;
        if (W < BASE && !fine3) return bisect(W, BASE);
        if (W >= top - 1e-9) return noneRes;
        return fineAt(top).then(function (fineTop) {
          if (my !== ctx.seq) return null;
          if (fineTop) return noneRes;
          return bisect(Math.max(W, BASE), top);
        });
      });
    });
  }

  /* ---- the tier, the note, the sheets, the write ---- */
  function tier(variants, w, h) {
    var mean = (w + h) / 2;
    /* size tiers are option 1 = a bare number ('2"', '2.5"'); '11" x 4" (Most Popular)' style options are fixed sizes, not tiers */
    var vs = (variants || []).map(function (v) { var m = /^\s*(\d+(?:\.\d+)?)\s*(?:"|″|in)?\s*$/.exec(String(v.option1 || '')); return { id: v.id, num: m ? parseFloat(m[1]) : NaN }; })
      .filter(function (v) { return v.num > 0; }).sort(function (a, b) { return a.num - b.num; });
    if (!vs.length) return null;
    for (var i = 0; i < vs.length; i++) if (mean <= vs[i].num) return vs[i];
    return vs[vs.length - 1];
  }
  /* C520 (unlock proportions, the ninjatransfers.com protocol — NT ntStretchRescale): a stretched line (_Stretched, proportions unlocked
     on the product page) carries its art already stretched to W x H. This editor keeps the line's own shape, so:
       - _Stretched names the new size (it is also a row on the build sheet)
       - a raw stretched _Upload (fit=scale, no build sheet) scales its px with the size: same shape, still 300 dpi
       - the build sheet's base for a classic line is its _cartImg, the stretch at thumb size (h=150): it is scaled up to the new size's
         300 dpi before the sheet is drawn, keeping its exact px shape (which already holds any measured background-removal margins) */
  function fitScale(url) {
    var m = /^([^?]*)\?(.*)$/.exec(String(url || ''));
    if (!m || !/(^|&)fit=scale(&|$)/.test(m[2])) return null;
    var q = m[2].split('&'), pw = 0, ph = 0;
    q.forEach(function (kv) { var a = kv.split('='); if (a[0] === 'w') pw = parseFloat(a[1]); if (a[0] === 'h') ph = parseFloat(a[1]); });
    return (pw > 0 && ph > 0) ? { base: m[1], q: q, pw: pw, ph: ph } : null;
  }
  function withPx(f, pw, ph) {
    var k = Math.min(1, 8192 / pw, 8192 / ph);
    pw = Math.max(1, Math.round(pw * k)); ph = Math.max(1, Math.round(ph * k));
    return f.base + '?' + f.q.filter(function (kv) { return !/^(w|h)=/.test(kv); }).concat(['w=' + pw, 'h=' + ph]).join('&');
  }
  function stretchRescale(props, w, h) {
    if (!props['_Stretched']) return;
    var old = String(props['_Stretched']).split(/\s*x\s*/i), ow = parseFloat(old[0]), oh = parseFloat(old[1]);
    var f = isSheet(props['_Upload']) ? null : fitScale(props['_Upload']);
    if (f && ow > 0 && oh > 0) props['_Upload'] = withPx(f, f.pw * w / ow, f.ph * h / oh);
    props['_Stretched'] = r2(w) + ' x ' + r2(h);
  }
  function stretchBase(props, b, h) {
    if (!props['_Stretched'] || b !== props['_cartImg']) return b;
    var f = fitScale(b);
    return f ? withPx(f, f.pw * (h * 300) / f.ph, h * 300) : b;
  }
  function sizeNote(props, w0, h0, w, h) {
    var nowS = fmt(w) + ' × ' + fmt(h) + ' in';
    if (props['_reorder'] != null && String(props['_reorder']).trim()) {
      var cur = String(props['_reorder']);
      var was = (/\(was ([^)]+)\)/.exec(cur) || [])[1] || (fmt(w0) + ' × ' + fmt(h0) + ' in');
      if (/^Design previously used on /i.test(cur)) {
        /* C456: a purchased design's reorder note ("Design previously used on #PATCH-…") keeps its wording first */
        props['_reorder'] = cur.replace(/\s+— similar reorder.*$/i, '') + ' — similar reorder, new size requested ' + nowS + ' (was ' + was + ')';
      } else {
        var orig = cur.replace(/^Similar reorder\s+/i, '').replace(/\s+— new size requested.*$/, '');
        props['_reorder'] = 'Similar reorder ' + orig + ' — new size requested ' + nowS + ' (was ' + was + ')';
      }
    } else {
      /* a normal order carries no note (C428d); a _Size Change left by the first C428 build comes off on the next edit */
      delete props['_Size Change'];
    }
  }
  function sheet(base, lines, notes, name) {
    return new Promise(function (res) {
      var ss = SS(); if (!ss || !base) { res(null); return; }
      var done = false, fin = function (u) { if (!done) { done = true; res(u || null); } };
      setTimeout(function () { fin(null); }, 25000);
      ss.compose(base, lines, notes, function (cv) {
        if (!cv) { fin(null); return; }
        ss.upload(cv, name, fin, { host: toHost });
      }, { host: toHost });
    });
  }
  function save(ctx, w, h, setStatus, verdictRes) {
    return Promise.all([j('/cart.js', { cache: 'no-store', credentials: 'same-origin' }), j('/products/' + encodeURIComponent(ctx.handle) + '.js')]).then(function (r) {
      var cart = r[0], prod = r[1];
      var line = (cart.items || []).filter(function (it) { return it.key === ctx.key; })[0];
      if (!line) throw new Error('This item changed — refresh the cart and try again.');
      /* /cart/change.js replaces the whole property set: the line goes back intact with only the size keys, the note and the sheets rewritten */
      var p = line.properties || {}, props = {};
      Object.keys(p).forEach(function (k) { props[k] = p[k]; });
      var v = tier(prod.variants, w, h);
      if (!v) throw new Error('This product has no size tiers to move to.');
      props['_Width (in)'] = w.toFixed(2);
      props['_Height (in)'] = h.toFixed(2);
      if ('Width (in)' in p) props['Width (in)'] = w.toFixed(2);
      if ('Height (in)' in p) props['Height (in)'] = h.toFixed(2);
      sizeNote(props, ctx.w0, ctx.h0, w, h);
      stretchRescale(props, w, h); /* C520 */
      if (ctx.pvc) { if (verdictRes && verdictRes.printedPvc) props['_Printed PVC'] = 'Yes'; else delete props['_Printed PVC']; } /* C441 */
      /* sheets: rebuilt from the line's own base images (AI render / digital preview), never from the old sheet */
      var ss = SS();
      var lines = ss ? ss.lines(props, ctx.typeName) : [];
      var notes = ss ? ss.notes(props) : '';
      var art = String(props['_Original Image'] || '').split('?')[0];
      var sheetKey = ((/ai-proof-(.+?)--spec-dk/.exec(String(props['_AI Proof'] || '')) || /ai-proof-(.+?)--spec-dk/.exec(String(props['_Upload'] || '')) || [])[1]) || 'patch';
      var name = function () { return 'ai-proof-' + sheetKey + '--spec-dk' + (ss ? ss.dk(art, w, h, lines) : '0') + '-' + Date.now() + '-' + Math.floor(Math.random() * 10000) + '.png'; };
      var needAi = isSheet(props['_AI Proof']);
      var needUp = isSheet(props['_Upload']);
      var aiBase = needAi ? String(props['_cartImg'] || '') : '';
      var upBase = needUp ? String(props['_Preview Image'] || '') : '';
      if (needUp && !upBase && !needAi) upBase = String(props['_cartImg'] || props['_Original Image'] || '');
      upBase = stretchBase(props, upBase, h); /* C520 */
      if (isSheet(aiBase)) aiBase = '';
      if (isSheet(upBase)) upBase = '';
      if ((needAi || needUp) && !ss) throw new Error('The build sheet tools did not load — refresh and try again.');
      setStatus('Updating your build sheet…');
      return Promise.all([aiBase ? sheet(aiBase, lines, notes, name()) : Promise.resolve(null), upBase ? sheet(upBase, lines, notes, name()) : Promise.resolve(null)]).then(function (sh) {
        var uA = sh[0], uB = sh[1];
        if ((aiBase && !uA) || (upBase && !uB)) throw new Error('Could not rebuild the build sheet — please try again.');
        if (uA) props['_AI Proof'] = uA;
        if (uB) props['_Upload'] = uB;
        else if (needUp && uA) props['_Upload'] = uA; /* one base only: the same sheet rides on both (builder contract) */
        /* C10: on a digital order _Mockup Catalog is the sheet of record (the _Upload sheet, else the AI one) — it follows the rebuild */
        if (isSheet(props['_Mockup Catalog']) && (uB || uA)) props['_Mockup Catalog'] = uB || uA;
        setStatus('Saving…');
        if (v.id === line.variant_id) return post('/cart/change.js', { id: line.key, quantity: line.quantity, properties: props });
        return post('/cart/add.js', { items: [{ id: v.id, quantity: line.quantity, properties: props }] }).then(function () {
          return post('/cart/change.js', { id: line.key, quantity: 0 });
        });
      });
    });
  }

  /* the line's own artwork + type decide what the gate judges — shared by the cart editor and npCartSizeApi (C453c) */
  function fillCtx(ctx, p, suffix) {
    var rawKey = lineTypeKey(p, suffix || '');
    ctx.judgeUrl = judgeUrl(p);
    ctx.typeKey = FAMILY[rawKey] || rawKey; /* the rule set the gate reads (woven-labels gates as woven-2-0), like TYPE_FAMILY */
    ctx.pvc = /^(rubber-pvc-2-0|keychains-2-0)$/.test(ctx.typeKey); /* C441 */
    if (NAMES[rawKey]) ctx.typeName = NAMES[rawKey];
    if (ctx.typeName) ctx.noun = nounFor(ctx.typeName);
    return ctx;
  }

  /* ---- the editor ---- */
  function open(row) {
    if (row.__npCs) { row.__npCs.hidden = !row.__npCs.hidden; if (!row.__npCs.hidden) { var f0 = row.__npCs.querySelector('[data-np-cs-w]'); if (f0 && !f0.disabled) f0.focus(); } return; }
    var w0 = parseFloat(row.getAttribute('data-np-cs-w')), h0 = parseFloat(row.getAttribute('data-np-cs-h'));
    if (!(w0 > 0 && h0 > 0)) return;
    var ctx = {
      key: row.getAttribute('data-np-cs'), handle: row.getAttribute('data-np-cs-handle'), typeName: row.getAttribute('data-np-cs-type') || '', noun: 'a patch',
      w0: w0, h0: h0, ratio: h0 / w0,
      min: parseFloat(row.getAttribute('data-np-cs-min')) || 0.5, max: parseFloat(row.getAttribute('data-np-cs-max')) || 15,
      judgeUrl: '', typeKey: '', mem: {}, at: {}, seq: 0, ready: false
    };
    var box = document.createElement('div');
    box.className = 'np-cs-box';
    box.innerHTML = '<div class="np-cs-fields">' +
      '<label class="np-cs-f"><span>Width</span><div><input type="number" inputmode="decimal" step="any" min="' + ctx.min + '" max="' + ctx.max + '" data-np-cs-w value="' + fmt(w0) + '" aria-label="Width in inches" disabled><em>in</em></div></label>' +
      '<span class="np-cs-x" aria-hidden="true">×</span>' +
      '<label class="np-cs-f"><span>Height</span><div><input type="number" inputmode="decimal" step="any" min="' + ctx.min + '" max="' + ctx.max + '" data-np-cs-h value="' + fmt(h0) + '" aria-label="Height in inches" disabled><em>in</em></div></label>' +
      '</div>' +
      '<p class="np-cs-hint">Width and height stay in proportion with your design.</p>' +
      '<p class="np-cs-msg" data-np-cs-msg role="status" aria-live="polite">Loading…</p>' +
      '<div class="np-cs-actions"><button type="button" class="np-cs-cancel" data-np-cs-cancel>Cancel</button><button type="button" class="np-cs-save" data-np-cs-save disabled>Update size</button></div>';
    /* its own full-width row in the item's grid: the details column is narrow and the quantity stepper overlaps it */
    var grid = row.closest('.cart-form-item--layout');
    if (grid) grid.appendChild(box); else row.insertAdjacentElement('afterend', box);
    row.__npCs = box;
    var wI = box.querySelector('[data-np-cs-w]'), hI = box.querySelector('[data-np-cs-h]'), msg = box.querySelector('[data-np-cs-msg]'), btn = box.querySelector('[data-np-cs-save]');
    var verdict = null, busy = false, jT = null;
    var say = function (html, state) { msg.innerHTML = html || ''; msg.hidden = !html; msg.setAttribute('data-state', state || ''); };
    var cur = function () { return { w: r2(parseFloat(wI.value)), h: r2(parseFloat(hI.value)) }; };
    var same = function (s) { return Math.abs(s.w - w0) < 0.005 && Math.abs(s.h - h0) < 0.005; };
    var refresh = function () {
      var s = cur();
      btn.disabled = busy || !ctx.ready || !(s.w > 0 && s.h > 0) || same(s) || !verdict || (verdict.state !== 'ok' && verdict.state !== 'warn') || verdict.w !== s.w || verdict.h !== s.h; /* C435: a warning still lets them continue */
    };
    var run = function () {
      var s = cur(); verdict = null; refresh();
      if (!(s.w > 0 && s.h > 0)) { say('Enter a width or a height.', 'block'); return; }
      if (same(s)) { say('', ''); return; }
      say('Checking ' + inch(s.w, s.h) + '…', 'pending');
      judge(ctx, s.w, s.h).then(function (res) {
        if (!res) return; /* superseded by a newer entry */
        var s2 = cur(); if (s2.w !== s.w || s2.h !== s.h) return;
        verdict = { state: res.state, w: s.w, h: s.h };
        say(res.text + (res.fixW ? ' <button type="button" class="np-cs-fix" data-np-cs-fix="' + res.fixW + '">Use ' + fmt(res.fixW) + '″ × ' + fmt(res.fixH) + '″</button>' : ''), res.state);
        refresh();
      });
    };
    var later = function () { clearTimeout(jT); verdict = null; refresh(); jT = setTimeout(run, 400); };
    wI.addEventListener('input', function () { var v = parseFloat(wI.value); if (v > 0) hI.value = fmt(r2(v * ctx.ratio)); later(); });
    hI.addEventListener('input', function () { var v = parseFloat(hI.value); if (v > 0) wI.value = fmt(r2(v / ctx.ratio)); later(); });
    /* the editor sits inside the cart <form>: Enter would submit it (implicit submission = the checkout button) */
    [wI, hI].forEach(function (inp) { inp.addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); clearTimeout(jT); if (!btn.disabled) btn.click(); else run(); } }); });
    box.addEventListener('click', function (e) {
      var fx = e.target.closest && e.target.closest('[data-np-cs-fix]'); if (!fx) return;
      e.preventDefault();
      var fw = parseFloat(fx.getAttribute('data-np-cs-fix'));
      wI.value = fmt(fw); hI.value = fmt(r2(fw * ctx.ratio)); clearTimeout(jT); run();
    });
    box.querySelector('[data-np-cs-cancel]').addEventListener('click', function () { clearTimeout(jT); wI.value = fmt(w0); hI.value = fmt(h0); verdict = null; say('', ''); refresh(); box.hidden = true; });
    btn.addEventListener('click', function () {
      var s = cur();
      if (btn.disabled) return;
      busy = true; refresh(); wI.disabled = hI.disabled = true;
      /* the verdict again right before the write (memoised, so instant) — a save only ever happens from an ok */
      judge(ctx, s.w, s.h).then(function (res) {
        if (!res || (res.state !== 'ok' && res.state !== 'warn')) throw new Error((res && res.text) || 'Please check the size and try again.');
        return save(ctx, s.w, s.h, function (t) { say(t, 'pending'); }, res);
      }).then(function () {
        say('Size updated — refreshing your cart…', 'ok');
        setTimeout(function () { window.location.reload(); }, 400);
      }).catch(function (e) {
        busy = false; wI.disabled = hI.disabled = false; verdict = null; refresh();
        say((e && e.message) || 'Could not update the size — please try again.', 'block');
      });
    });
    /* the line's own artwork + type decide what the gate judges */
    j('/cart.js', { cache: 'no-store', credentials: 'same-origin' }).then(function (cart) {
      var line = (cart.items || []).filter(function (it) { return it.key === ctx.key; })[0];
      if (!line) throw new Error('gone');
      fillCtx(ctx, line.properties || {}, row.getAttribute('data-np-cs-suffix') || '');
    }).catch(function () {}).then(function () {
      ctx.ready = true; wI.disabled = hI.disabled = false; say('', ''); refresh();
      if (ctx.judgeUrl) gateAt(ctx, 3); /* warm the calibrated frame while they type */
      try { wI.focus(); wI.select(); } catch (eF) {}
    });
  }

  document.addEventListener('click', function (e) {
    var b = e.target.closest && e.target.closest('[data-np-cs-open]');
    if (!b) return;
    e.preventDefault();
    var row = b.closest('[data-np-cs]');
    if (row) open(row);
  });

  /* C453c (Justin 2026-09-29, My Designs' Last Ordered "Edit size should open like this"): the same rules for a size chosen
     OUTSIDE the cart — My Designs' Edit Size dialog judges with judge() and, once the reorder line is in the cart, writes the
     new size with save() (tier, every property, the rebuilt sheets, the "Similar reorder … new size requested" note). One
     editor's rules, two doors. ctx(opts) takes the ORDER line's properties; ctx.key is set once the line is in the cart. */
  window.npCartSizeApi = {
    ctx: function (o) {
      var w0 = parseFloat(o.w0), h0 = parseFloat(o.h0);
      var c = { key: o.key || '', handle: o.handle || '', typeName: o.typeName || '', noun: 'a patch', w0: w0, h0: h0, ratio: h0 / w0,
                min: parseFloat(o.min) || 0.5, max: parseFloat(o.max) || 15, judgeUrl: '', typeKey: '', mem: {}, at: {}, seq: 0, ready: true };
      fillCtx(c, o.props || {}, o.suffix || '');
      if (c.judgeUrl) gateAt(c, 3); /* warm the calibrated frame */
      return c;
    },
    judge: judge,
    save: save,
    tier: tier, /* C453i: the size tier a W × H lands on (My Designs' price preview) */
    fmt: fmt
  };
})();
